import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FileInputComponent } from '@catch/file-input';
import { ButtonComponent, EmptyPlaceholderComponent, InlineInfoComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { map } from 'rxjs';
import { viewLoader } from '../../../../../../assets/animations/animations';
import { fileTypes } from '../../../../../constants/filetypes';
import { PlatformProfileService } from '../../../../services/platform-profile.service';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';

@Component({
  selector: 'app-platform-manual-apply-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    ButtonComponent,
    EmptyPlaceholderComponent,
    FileInputComponent,
    InlineInfoComponent,
    TranslocoPipe,
  ],
  templateUrl: './platform-manual-apply-view.component.html',
  styleUrl: './platform-manual-apply-view.component.scss',
  animations: [viewLoader],
})
export class PlatformManualApplyViewComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobs = inject(PlatformJobsService);
  private readonly profile = inject(PlatformProfileService);
  private readonly toastsService = inject(ToastsService);

  readonly fileTypes = fileTypes;
  readonly icons = { search: faSearch };
  readonly submitAttempt = signal(false);

  private readonly jobId = toSignal(
    this.route.paramMap.pipe(map((params) => Number(params.get('jobId')))),
    { initialValue: Number(this.route.snapshot.paramMap.get('jobId')) }
  );

  readonly job = computed(() => {
    const id = this.jobId();
    if (!Number.isFinite(id)) {
      return undefined;
    }
    return this.jobs.boardJobById(id);
  });

  readonly form = this.fb.group({
    firstName: this.fb.control('', Validators.required),
    lastName: this.fb.control('', Validators.required),
    email: this.fb.control('', Validators.required),
    phone: this.fb.control(''),
    cv: this.fb.control<File | null>(null, Validators.required),
    privacyAccepted: this.fb.control(false, Validators.requiredTrue),
  });

  constructor() {
    const id = Number(this.route.snapshot.paramMap.get('jobId'));
    if (Number.isFinite(id)) {
      this.jobs.loadBoardJob(id);
    }
  }

  /**
   * CV file control for the shared file input.
   * @returns CV form control
   */
  cvControl(): UntypedFormControl {
    return this.form.controls['cv'] as UntypedFormControl;
  }

  /**
   * Submits the manual application and returns to the job list.
   */
  submit(): void {
    this.submitAttempt.set(true);
    const job = this.job();
    if (this.form.invalid || !job) {
      return;
    }
    const cv = this.form.controls['cv'].value;
    this.jobs.submitApplyWithProfile(this.jobs.applyContextFromBoardJob(job), {
      firstName: this.form.controls['firstName'].value || undefined,
      lastName: this.form.controls['lastName'].value || undefined,
      phone: this.form.controls['phone'].value || undefined,
      skills: this.profile.profile().skills,
      cvFile: cv instanceof File ? cv : null,
    });
    this.toastsService.success('jobboard.platform.apply-with-profile.submit-toast');
    void this.router.navigate(['/jobs']);
  }
}
